
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Save, X, BarChart, Leaf, FileText, Upload } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CarbonFootprintTab from './CarbonFootprintTab';
import ESGReportsTab from './ESGReportsTab';
import DataImportTab from './DataImportTab';

interface SustainabilitySettingsProps {
  editing: boolean;
  onEditToggle: () => void;
  onSave: () => void;
}

const SustainabilitySettings: React.FC<SustainabilitySettingsProps> = ({ 
  editing, 
  onEditToggle, 
  onSave 
}) => {
  const [activeTab, setActiveTab] = useState("carbon");
  
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Sustainability</h2>
          <p className="text-muted-foreground">
            Configure sustainability and ESG reporting settings
          </p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={onEditToggle} className="gap-1">
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={onSave} className="gap-1">
                <Save className="h-4 w-4" />
                Save Changes
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={onEditToggle} className="gap-1">
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 mb-6">
            <TabsTrigger value="carbon" className="flex items-center gap-1">
              <Leaf className="h-4 w-4" />
              Carbon Footprint
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              ESG Reports
            </TabsTrigger>
            <TabsTrigger value="import" className="flex items-center gap-1">
              <Upload className="h-4 w-4" />
              Data Import
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="carbon" className="space-y-4">
            <CarbonFootprintTab editing={editing} />
          </TabsContent>
          
          <TabsContent value="reports" className="space-y-4">
            <ESGReportsTab editing={editing} />
          </TabsContent>
          
          <TabsContent value="import" className="space-y-4">
            <DataImportTab editing={editing} />
          </TabsContent>
        </Tabs>
        
        {!editing && (
          <div className="flex justify-center items-center mt-6">
            <Link to="/sustainability/dashboard">
              <Button variant="default" className="gap-2">
                <BarChart className="h-4 w-4" />
                View Sustainability Dashboard
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SustainabilitySettings;
